import React, { type ReactNode } from "react";
import classNames from "classnames";
import t from "../../../../../../../../../../i18n";
import { getFaultStatusText, getFaultStatusTextIcon } from "../../../../../../../../Fault";
import type { FailureValueStatus } from "../../../../../../../../../../api/useFailureMetadata";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import Text from "@ui/Text";
import type { FaultEvent } from "../../../../../../../../../../api/useFaultEvents";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { color } from "@ui";
import Button from "@ui/Button";
import { AlertFill, Repeat2Fill } from "@ui/IconV2/react";
import { useCanUserDoOnProject } from "../../../../../../../../../canUserDo";

const Card = ({
  projectId,
  event,
  currentFaultStatus,
  analysesGroupedByFaultStatus,
  handleSubmit,
  actionButton,
}: {
  projectId: string;
  event: FaultEvent;
  currentFaultStatus: FailureValueStatus;
  analysesGroupedByFaultStatus: {
    inZoom: {
      [faultStatus in FailureValueStatus]: {
        date: number;
      }[];
    };
    offZoom: {
      [faultStatus in FailureValueStatus]: {
        date: number;
      }[];
    };
  };
  handleSubmit: ({
    currentFaultStatus,
    newFaultStatus,
    applyToNewDetection,
  }: {
    currentFaultStatus: FailureValueStatus;
    newFaultStatus: FailureValueStatus;
    applyToNewDetection: boolean;
  }) => Promise<boolean>;
  actionButton: ReactNode;
}) => {
  const hasWriteAccessToProject = useCanUserDoOnProject("PROJECT_WRITE", projectId);
  const impactedAnalysesByStatusChange = analysesGroupedByFaultStatus["inZoom"][currentFaultStatus];

  if (impactedAnalysesByStatusChange === undefined || impactedAnalysesByStatusChange.length === 0)
    return null;

  const name = getFaultStatusText(currentFaultStatus);
  const title = t("project.fault_event.evolution_chart.batch_status_change.title", {
    status: name,
    count: impactedAnalysesByStatusChange.length,
  });

  const icon = getFaultStatusTextIcon(currentFaultStatus);
  const iconAlert =
    currentFaultStatus === "LEGACY_REJECTED" ? (
      <TooltipV2
        placement="top"
        overlay={
          <OverlayWrapper
            text={
              <div className={maxOverlay}>
                <Text type="body2">
                  {t(
                    "project.fault_event.evolution_chart.batch_status_change.alert_icon.tooltip.text",
                  )}
                </Text>
                {event.metadata.status !== "CLOSED" && hasWriteAccessToProject ? (
                  <Button
                    className={linkButton}
                    variant="link"
                    onClick={() =>
                      handleSubmit({
                        currentFaultStatus,
                        newFaultStatus: "REJECTED",
                        applyToNewDetection: false,
                      })
                    }
                  >
                    <Text type="body2">
                      {t(
                        "project.fault_event.evolution_chart.batch_status_change.alert_icon.tooltip.linkText",
                      )}
                    </Text>
                  </Button>
                ) : null}
              </div>
            }
          />
        }
      >
        <div className={classNames(spacingIcon, centerIcon)}>
          <AlertFill color={color.phosphorus400} size="s" />
        </div>
      </TooltipV2>
    ) : null;

  const repeatStatusOnFaultEvent = event.metadata.newDiagnosesStatus;
  const iconRepeat =
    repeatStatusOnFaultEvent === currentFaultStatus ? (
      <TooltipV2
        placement="top"
        overlay={
          <OverlayWrapper
            text={
              <div className={maxOverlay}>
                <Text type="body2">
                  {t(
                    "project.fault_event.evolution_chart.batch_status_change.repeat_icon.tooltip.text",
                  )}
                </Text>
              </div>
            }
          />
        }
      >
        <div className={classNames(spacingIcon, centerIcon)}>
          <Repeat2Fill color={color.lightNitrogen500_Main} size="s" />
        </div>
      </TooltipV2>
    ) : null;

  return (
    <div className={batchFaultValueStatusChangeCard} key={currentFaultStatus}>
      <div className={headerCard}>
        <div className={center}>
          <div className={titleCard}>
            <div className={classNames(iconCard)}>{icon}</div>
            <Text type="body1SemiBold">{title}</Text>
          </div>
          {iconAlert}
        </div>
        {iconRepeat}
      </div>
      {event.metadata.status !== "CLOSED" && hasWriteAccessToProject ? actionButton : null}
    </div>
  );
};

export default Card;

// #region CSS
const batchFaultValueStatusChangeCard = css`
  max-width: 300px;
  min-width: 230px;
  flex-grow: 1;

  border-radius: 4px;
  background-color: ${color.lightNitrogen50};
  border: 1px solid ${color.lightNitrogen100};
  padding: ${spacings.m} ${spacings.s};

  & > :nth-child(2) {
    /* button */
    margin-top: ${spacings.s};
    width: 100%;
  }
`;

const linkButton = css`
  height: auto;
`;

const centerIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const iconCard = css`
  display: flex;
  align-items: center;
`;

const headerCard = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const titleCard = css`
  display: flex;
  align-items: center;

  & > :nth-child(2) {
    /* status */
    margin-left: ${spacings.xxs};
  }
`;

const center = css`
  display: flex;
  align-items: center;
`;

const spacingIcon = css`
  margin-left: ${spacings.s};
`;

const maxOverlay = css`
  max-width: 199px;

  & > :nth-child(2) {
    /** button option */
    margin-top: ${spacings.xs};
  }
`;
// #endregion CSS
